import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "@/components/Footer";

/**
 * WhatsAppFab — floating "chat with us on WhatsApp" button, bottom-right.
 * Uses the centralised `COMPANY.whatsappHref` from Footer.jsx so the phone
 * number stays in one place. Hidden on partner/admin routes to keep those
 * dashboards uncluttered.
 *
 * Behaviour:
 *   - Fades in 1.5s after page-load to avoid competing with hero CTAs.
 *   - Pre-fills a friendly message so the user just taps "Send".
 *   - Green FAB with WhatsApp icon + gentle pulse ring for attention.
 *   - Reactive to route changes — hides immediately when landing on partner/admin.
 */
export default function WhatsAppFab() {
  const [readyForDelay, setReadyForDelay] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => setReadyForDelay(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const isHiddenRoute =
    pathname.startsWith("/partner") ||
    pathname.startsWith("/admin") ||
    pathname === "/ops" ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/register");

  if (!readyForDelay || isHiddenRoute) return null;

  const msg = encodeURIComponent(
    "Hi SwaanStay team, I need help with my booking / hotel search. Please assist."
  );
  const href = `${COMPANY.whatsappHref}?text=${msg}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-fab"
      aria-label="Chat with SwaanStay support on WhatsApp"
      className="fixed z-40 bottom-24 md:bottom-6 right-4 md:right-6 group"
    >
      <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
      <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_10px_28px_-6px_rgba(37,211,102,0.55)] hover:scale-110 active:scale-95 transition-transform">
        <svg viewBox="0 0 32 32" fill="currentColor" className="w-7 h-7">
          <path d="M16.001 3C9.373 3 4 8.373 4 15c0 2.35.68 4.545 1.855 6.402L4 29l7.777-1.813A11.949 11.949 0 0016 27c6.627 0 12-5.373 12-12S22.628 3 16.001 3zm0 21.6a9.6 9.6 0 01-4.881-1.335l-.35-.207-4.618 1.077 1.099-4.5-.228-.36A9.6 9.6 0 1116.001 24.6zm5.28-7.19c-.288-.144-1.702-.842-1.966-.937-.263-.096-.455-.144-.647.144-.192.288-.744.937-.912 1.129-.168.192-.336.216-.624.072-.288-.144-1.212-.446-2.31-1.42-.854-.762-1.43-1.702-1.597-1.99-.168-.288-.018-.443.126-.587.13-.13.288-.336.432-.504.144-.168.192-.288.288-.48.096-.192.048-.36-.024-.504-.072-.144-.647-1.56-.887-2.136-.234-.56-.472-.484-.647-.493l-.552-.01c-.192 0-.504.072-.768.36-.264.288-1.008.984-1.008 2.4 0 1.416 1.032 2.784 1.176 2.976.144.192 2.03 3.096 4.918 4.343.687.297 1.223.474 1.641.607.69.22 1.317.19 1.813.115.553-.083 1.702-.696 1.942-1.368.24-.672.24-1.248.168-1.368-.072-.12-.264-.192-.552-.336z" />
        </svg>
      </span>
    </a>
  );
}
